import {
  $create,
  $getState,
  $setState,
  createState,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  ElementNode,
  LexicalNode,
} from 'lexical';

function isAccepted(value: unknown): value is true {
  if (typeof value === 'string') {
    return value === 'true';
  }

  if (value != null) {
    return Boolean(value);
  }

  return false;
}

const suggestionState = createState<'suggestion-accepted-node', boolean | null>(
  'suggestion-accepted-node',
  {
    parse: value => isAccepted(value),
    unparse: (value: boolean | null) => value,
  },
);

export class SuggestionNode extends ElementNode {
  $config() {
    return this.config('suggestion', {
      extends: ElementNode,
      stateConfigs: [{ flat: true, stateConfig: suggestionState }],
    });
  }

  static importDOM(): DOMConversionMap | null {
    return {
      suggestion: () => ({
        conversion: (): DOMConversionOutput => {
          const node = $createSuggestionNode();
          return { node };
        },
        priority: 0,
      }),
    };
  }

  createDOM(): HTMLElement {
    const container = document.createElement('div');
    container.style.backgroundColor = 'var(--mantine-color-success-1)';
    container.style.borderRadius = '8px';
    container.style.padding = '8px 8px 36px 8px';
    container.style.position = 'relative';
    container.style.border = '1px solid var(--mantine-color-success-3)';

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'suggestion-buttons';
    buttonContainer.style.position = 'absolute';
    buttonContainer.style.bottom = '8px';
    buttonContainer.style.right = '8px';
    buttonContainer.style.display = 'flex';
    buttonContainer.style.gap = '6px';
    buttonContainer.style.flexShrink = '0';

    const acceptButton = this.createButton('Accept', 'accept', {
      style: {
        backgroundColor: 'var(--mantine-color-success-6)',
      },
      events: {
        mouseenter: () => {
          acceptButton.style.backgroundColor = 'var(--mantine-color-success-7)';
        },
        mouseleave: () => {
          acceptButton.style.backgroundColor = 'var(--mantine-color-success-6)';
        },
      },
    });

    const rejectButton = this.createButton('Reject', 'reject', {
      style: {
        backgroundColor: 'var(--mantine-color-error-6)',
      },
      events: {
        mouseenter: () => {
          rejectButton.style.backgroundColor = 'var(--mantine-color-error-7)';
        },
        mouseleave: () => {
          rejectButton.style.backgroundColor = 'var(--mantine-color-error-6)';
        },
      },
    });

    buttonContainer.appendChild(acceptButton);
    buttonContainer.appendChild(rejectButton);
    container.appendChild(buttonContainer);

    return container;
  }

  updateDOM(prevNode: this, dom: HTMLElement): boolean {
    const currentState = $getState(this, suggestionState);
    const buttonContainer = dom.querySelector('.suggestion-buttons');

    if (isAccepted(currentState)) {
      if (buttonContainer) {
        (buttonContainer as HTMLElement).style.display = 'none';
      }
      dom.style.backgroundColor = 'transparent';
      dom.style.border = 'none';
      dom.style.padding = '0';
      dom.style.position = 'static';
    } else if (currentState === false) {
      dom.style.display = 'none';
    } else {
      if (buttonContainer) {
        (buttonContainer as HTMLElement).style.display = 'flex';
      }
      dom.style.backgroundColor = 'var(--mantine-color-success-1)';
      dom.style.border = '1px solid var(--mantine-color-success-3)';
      dom.style.borderRadius = '8px';
      dom.style.padding = '8px 8px 36px 8px';
      dom.style.position = 'relative';
      dom.style.display = '';
    }

    return false;
  }

  exportDOM(): DOMExportOutput {
    const currentState = $getState(this, suggestionState);

    if (!isAccepted(currentState) && currentState !== null) {
      return { element: null };
    }

    const container = document.createElement(isAccepted(currentState) ? 'div' : 'suggestion');
    return { element: container };
  }

  private createButton(
    text: string,
    intent: 'accept' | 'reject',
    options: {
      style: Record<string, string>;
      events: Record<string, () => void>;
    },
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.dataset.intent = intent;

    const baseStyles = {
      fontSize: '12px',
      fontWeight: '600',
      height: '24px',
      minWidth: '52px',
      color: 'var(--mantine-color-white)',
      border: 'none',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: '8px',
      fontFamily: 'Manrope',
      transition: 'background-color 0.2s ease',
      lineHeight: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    Object.assign(button.style, baseStyles);
    Object.assign(button.style, options.style);
    button.textContent = text;
    Object.entries(options.events).forEach(([event, handler]) => {
      button.addEventListener(event, handler);
    });

    return button;
  }
}

export function $createSuggestionNode(): SuggestionNode {
  return $setState($create(SuggestionNode), suggestionState, null);
}

export function $isSuggestionNode(node: LexicalNode | null | undefined): node is SuggestionNode {
  return node instanceof SuggestionNode;
}

export function $acceptSuggestion(node: SuggestionNode): void {
  $setState(node, suggestionState, true);
}

export function $rejectSuggestion(node: SuggestionNode): void {
  $setState(node, suggestionState, false);
}

export function $isSuggestionAccepted(node: SuggestionNode): boolean {
  const value = $getState(node, suggestionState);
  return isAccepted(value);
}

export function $isRejectedSuggestion(node: SuggestionNode) {
  const value = $getState(node, suggestionState);
  return !isAccepted(value) && value !== null;
}
